import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { getErrorMessageFromCode, CarrotErrorCodes } from "#/utils/CarrotErrors"
import { HttpErrors } from "#/utils/HttpErrors"
import { useHistory, useLocation } from "react-router-dom"
import queryString from "query-string"
import { useDidMount } from "../../../utils/Hooks"
import { useDispatch } from "react-redux"
import { redirectToSamlExternalLogin } from "../../../utils/RedirectToSamlExternalLogin"
import { persistEmail } from "#/redux/actions/loginActions"
import { PreloginOutcome } from "#/lib/carrot-api/types/Prelogin"
import { useQueryClient } from "@tanstack/react-query"
import { defineMessage, MessageDescriptor, useIntl } from "react-intl"
import { Link } from "@carrotfertility/carotene-core"
import { Form, FormOnSubmitHandler } from "@carrotfertility/carotene-core-x"
import { EmployeeSupportUrl } from "../../../utils/EmployeeSupportLink"
import { carrotClient } from "#/utils/CarrotClient"
import { OpenIDSignIn } from "#/pages/openid-sign-in"
import Settings from "#/utils/CarrotConfig"

export default function OpenIDLoginContainer() {
  const location = useLocation()
  const history = useHistory()
  const [state, setState] = useState({
    username: null as string,
    password: null as string,
    errorMessage: null as MessageDescriptor,
    returnUrl: null as string,
    isErrorPage: false,
    submitting: false,
    showForgotPassword: false,
    showPasswordInput: false,
    showEmailInput: true,
    samlConfig: null,
    shake: false,
    errorHasLink: false,
    showSsoAlert: false,
    showSubmitButton: true
  })
  const passwordFailedRef = useRef(false)
  const dispatch = useDispatch()
  const passwordInputRef = useRef<HTMLInputElement>()
  const queryClient = useQueryClient()

  const intl = useIntl()

  useEffect(() => {
    queryClient.clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  const onSubmit: FormOnSubmitHandler<{ username: string; password?: string }> = async (formData) => {
    if (formData.password) {
      await openIdLogin(formData.username, formData.password)
    } else {
      await preloginLookup(formData.username)
    }
  }

  useDidMount(() => {
    const parsedQueryString = queryString.parse(location.search)
    const [errorCode] = Array.isArray(parsedQueryString.errorCode)
      ? parsedQueryString.errorCode
      : [parsedQueryString.errorCode]
    const isErrorPage = errorCode != null

    const [returnUrl] = Array.isArray(parsedQueryString.ReturnUrl)
      ? parsedQueryString.ReturnUrl
      : [parsedQueryString.ReturnUrl]

    setState({
      ...state,
      errorMessage: errorCode ? getErrorMessageFromCode(errorCode) : null,
      // If we have an error message from the URL upon loading this view, that means we want to display only the message and "Sign in" button, so showEmailInput should be false
      showEmailInput: !isErrorPage,
      isErrorPage,
      returnUrl: returnUrl || null,
      errorHasLink:
        errorCode === CarrotErrorCodes.EMPLOYEE_INACTIVE || !!CarrotErrorCodes.SAML_UNSUCCESSFUL_OPERATION_EXCEPTION
    })
  })

  useLayoutEffect(() => {
    if (passwordInputRef.current) {
      passwordInputRef.current.focus()
    }
  }, [state.showEmailInput])

  const preloginLookup = async (email: string) => {
    if (state.isErrorPage) {
      setState({
        ...state,
        isErrorPage: false,
        showEmailInput: true,
        showForgotPassword: false,
        showPasswordInput: false,
        errorMessage: null,
        errorHasLink: false
      })
      return
    }

    setState({
      ...state,
      submitting: true,
      shake: false,
      errorHasLink: false
    })

    let response
    try {
      response = await carrotClient.prelogin(email)
    } catch (error) {
      setState({
        ...state,
        submitting: false,
        errorMessage: defineMessage({ defaultMessage: "Connection problem" }),
        shake: true,
        errorHasLink: false
      })
      return
    }

    switch (response.outcome) {
      case PreloginOutcome.SsoRedirect:
        redirectToSamlExternalLogin(response.sso.idpEntityId, state.returnUrl)
        return

      case PreloginOutcome.SsoIdpOnly:
        showIdpOnlySsoScreen()
        return

      case PreloginOutcome.Unregistered:
        dispatch(persistEmail(email))
        history.push(`/signup`)
        return

      case PreloginOutcome.Password:
      default:
        dispatch(persistEmail(email))
        setState({
          ...state,
          submitting: false,
          showPasswordInput: true,
          showForgotPassword: true,
          samlConfig:
            response.outcome === PreloginOutcome.Password && response.sso
              ? {
                  samlIdpEntityId: response.sso.idpEntityId
                }
              : null,
          errorMessage: null,
          shake: false,
          errorHasLink: false,
          showSsoAlert: false,
          showSubmitButton: true
        })

        if (passwordInputRef.current) {
          passwordInputRef.current.focus()
        }
        return
    }
  }

  const showIdpOnlySsoScreen = () => {
    setState({
      ...state,
      submitting: false,
      showEmailInput: false,
      showPasswordInput: false,
      showForgotPassword: false,
      samlConfig: null,
      errorMessage: null,
      shake: false,
      errorHasLink: false,
      showSsoAlert: true,
      showSubmitButton: false
    })
  }

  const openIdLogin = async (email: string, password?: string) => {
    setState({
      ...state,
      submitting: true,
      shake: false
    })

    try {
      await carrotClient.openIdLogin(email, password)

      if (state.returnUrl) {
        window.location.href = Settings.CARROT_BACKEND_URL + state.returnUrl
      }
    } catch (error) {
      const isDeactivated = error.name === HttpErrors.FORBIDDEN
      const isWrongPassword = error.name === HttpErrors.BAD_REQUEST

      setState({
        ...state,
        submitting: false,
        errorMessage: isDeactivated
          ? defineMessage({
              defaultMessage:
                "It looks like your account has recently been deactivated. If you need to submit receipts for service prior to leaving your benefit sponsor, please <link>contact us</link>. If you feel that your account has been deactivated in error, please reach out to your internal benefits team for next steps."
            })
          : isWrongPassword
          ? defineMessage({ defaultMessage: "Password does not match." })
          : defineMessage({
              defaultMessage:
                "Oops, something went wrong. Please try again later or <link>contact us</link> for assistance."
            }),
        shake: true,
        errorHasLink: !isWrongPassword
      })

      passwordFailedRef.current = true
      return
    }
  }

  const errorMessageElement = state.errorMessage?.id
    ? intl.formatMessage(state.errorMessage, {
        link: (linkContent) => (
          <Link style={{ color: "inherit" }} href={EmployeeSupportUrl} target={"_blank"}>
            {linkContent}
          </Link>
        )
      })
    : ""

  return (
    <Form onSubmit={onSubmit}>
      <OpenIDSignIn
        errorMessage={errorMessageElement}
        shake={state.shake}
        showEmailInput={state.showEmailInput}
        showForgotPassword={state.showForgotPassword}
        showPasswordInput={state.showPasswordInput}
        submitting={state.submitting}
        samlConfig={state.samlConfig}
        returnUrl={state.returnUrl}
        showSsoAlert={state.showSsoAlert}
        showSubmitButton={state.showSubmitButton}
      />
    </Form>
  )
}
